// utils/zoneUtils.js
// Zone creation utilities leveraging Redux for data access

import axios from '../axios/axiosConfig';

/**
 * Check general info record count from Redux store (preferred) or API
 * @param {object} jobDetailsState - Redux jobDetails state (from selectJobDetails)
 * @param {string} generalProjectApiUrl - API URL (fallback if Redux empty)
 * @param {string} jobId - Job ID
 * @returns {object} { success, count, error }
 */
export const checkGeneralInfoRecordCount = async (jobDetailsState, generalProjectApiUrl, jobId) => {
  try {
    // First, try to get data from Redux store (fast, no API call)
    const generalProject = jobDetailsState?.generalProject || [];
    
    if (generalProject.length > 0) {
      const jobRecords = generalProject.filter(record => 
        String(record.ProjectID) === String(jobId) || String(record.ID) === String(jobId)
      );
      return { success: true, count: jobRecords.length, source: 'redux' };
    }
    
    // Fallback to API call if Redux is empty
    const response = await axios.get(`${generalProjectApiUrl}?id=${jobId}`);
    
    if (response.status === 200 && response.data) {
      // If response.data is an array, return its length
      if (Array.isArray(response.data)) {
        return { success: true, count: response.data.length, source: 'api' };
      }
      // If response.data is a single object, count is 1
      else if (response.data && typeof response.data === 'object') {
        return { success: true, count: 1, source: 'api' };
      }
      // If no data, count is 0
      else {
        return { success: true, count: 0, source: 'api' };
      }
    }
    
    return { success: false, error: `API returned status ${response.status}` };
  } catch (error) {
    console.error("Error checking general info record count:", error);
    return { 
      success: false, 
      error: error.response?.data?.message || error.message || "Unknown error"
    };
  }
};

/**
 * Check if Zone 2 exists from Redux store (preferred) or API
 * @param {object} jobDetailsState - Redux jobDetails state (from selectJobDetails)
 * @param {string} generalStationApiUrl - API URL (fallback if Redux empty)
 * @param {string} jobId - Job ID
 * @returns {object} { success, zone2Exists, totalRecords, zones, error }
 */
export const checkZone2Exists = async (jobDetailsState, generalStationApiUrl, jobId) => {
  try {
    // First, try to get data from Redux store (fast, no API call)
    const generalStation = jobDetailsState?.generalStation || [];
    
    if (generalStation.length > 0) {
      const jobRecords = generalStation.filter(record => 
        String(record.ProjectID) === String(jobId)
      );
      
      const zone2Exists = jobRecords.some(record => record.Zone === 2);
      const zones = [...new Set(jobRecords.map(record => record.Zone).filter(Boolean))];
      
      return { 
        success: true, 
        zone2Exists,
        totalRecords: jobRecords.length,
        zones,
        source: 'redux'
      };
    }
    
    // Fallback to API call if Redux is empty
    const response = await axios.get(`${generalStationApiUrl}?projectID=${jobId}`);
    
    if (response.status === 200 && response.data) {
      let data = response.data;
      
      // Ensure data is an array
      if (!Array.isArray(data)) {
        data = [data];
      }
      
      // Check if any record has Zone = 2
      const zone2Exists = data.some(record => record.Zone === 2 || record.zone === 2);
      
      return { 
        success: true, 
        zone2Exists,
        totalRecords: data.length,
        zones: data.map(record => record.Zone || record.zone).filter(Boolean),
        source: 'api'
      };
    }
    
    return { success: false, error: `API returned status ${response.status}` };
  } catch (error) {
    console.error("Error checking Zone 2 existence:", error);
    return { 
      success: false, 
      error: error.response?.data?.message || error.message || "Unknown error"
    };
  }
};

/**
 * Check if Zone 2 can be created
 * Uses Redux store data when available to avoid unnecessary API calls
 * @param {object} jobDetailsState - Redux jobDetails state (from selectJobDetails)
 * @param {string} generalProjectApiUrl - General project API URL
 * @param {string} generalStationApiUrl - General station API URL
 * @param {string} jobId - Job ID
 * @returns {object} { canCreate, reason, generalInfoCount, existingZones }
 */
export const checkCanCreateZone2 = async (jobDetailsState, generalProjectApiUrl, generalStationApiUrl, jobId) => {
  try {
    // Check both general info record count and existing Zone 2 data
    // Pass Redux state to avoid duplicate API calls
    const [generalInfoResult, zone2Result] = await Promise.all([
      checkGeneralInfoRecordCount(jobDetailsState, generalProjectApiUrl, jobId),
      checkZone2Exists(jobDetailsState, generalStationApiUrl, jobId)
    ]);
    
    if (!generalInfoResult.success) {
      return { 
        canCreate: false, 
        reason: `Error checking general info: ${generalInfoResult.error}` 
      };
    }
    
    if (!zone2Result.success) {
      return { 
        canCreate: false, 
        reason: `Error checking Zone 2 data: ${zone2Result.error}` 
      };
    }
    
    // Check if Zone 2 already exists
    if (zone2Result.zone2Exists) {
      return { 
        canCreate: false, 
        reason: `Zone 2 already exists for this job. Existing zones: [${zone2Result.zones.join(', ')}]` 
      };
    }
    
    // Check if maximum records reached
    if (generalInfoResult.count >= 2) {
      return { 
        canCreate: false, 
        reason: `Maximum of 2 records already exist in general-info table. Current count: ${generalInfoResult.count}` 
      };
    }
    
    return { 
      canCreate: true, 
      generalInfoCount: generalInfoResult.count,
      existingZones: zone2Result.zones
    };
    
  } catch (error) {
    console.error("Error in checkCanCreateZone2:", error);
    return { 
      canCreate: false, 
      reason: `Unexpected error: ${error.message}` 
    };
  }
};

const toPascalPayloadKey = (key) => {
  if (key === 'projectID' || key === 'projectId' || key === 'ProjectId') {
    return 'ProjectID';
  }
  if (key === 'saleQuoteId') {
    return 'SaleQuoteId';
  }
  return key ? `${key.charAt(0).toUpperCase()}${key.slice(1)}` : key;
};

export const toPascalCasePayload = (payload = {}) => {
  return Object.entries(payload).reduce((acc, [key, value]) => {
    acc[toPascalPayloadKey(key)] = value;
    return acc;
  }, {});
};

export const buildZoneCreationPayload = (templateData = {}, jobId, zone, additionalData = {}) => {
  const transformedJobData = Object.keys(templateData).reduce((acc, key) => {
    if (key !== 'ID') {
      acc[toPascalPayloadKey(key)] = null;
    }
    return acc;
  }, {});

  return {
    ...transformedJobData,
    ProjectID: jobId,
    Zone: zone,
    SaleQuoteId: templateData.SaleQuoteId ?? templateData.saleQuoteId ?? null,
    ...toPascalCasePayload(additionalData)
  };
};

export const createZoneData = async (apiUrl, jobData, jobId, zone) => {
  try {
    const transformedJobData = buildZoneCreationPayload(jobData, jobId, zone);
  
    // Make the POST request with the transformed data using axios
    const response = await axios.post(apiUrl, transformedJobData);
    
    if (response.status === 200 || response.status === 201) {
      return { success: true, data: response.data };
    } else {
      return { success: false, error: response.statusText };
    }
  } catch (error) {
    console.error("Error creating zone data:", error);
    return { 
      success: false, 
      error: error.response?.data?.message || error.message || "Unknown error"
    };
  }
};
